import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import type { UploadDay, UploadItem, UploadManifest } from "../types";
import { fmtDT } from "../utils/period";

// «Управление → Данные»: one uploader for every source. The file (or a .zip of
// files) is first read on the server — what each file is, its period, what of it
// is stored already, day by day — and only «Загрузить» writes, in the chosen mode
// (only what is missing / replace the period) and range down to the second.
const SLOT_COLOR: Record<string, string> = { "1": "#34c759", "2": "rgba(127,127,127,0.38)", "3": "#a8dcb3" };
// «+1:30» stays left to right inside Farsi text (an isolate; otherwise it reads «1:30+»)
const ltr = (s: string) => String.fromCharCode(0x2066) + s + String.fromCharCode(0x2069);
const fmtInt = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

function DayBar({ d }: { d: UploadDay }) {
  const segs: { a: number; b: number; c: string }[] = [];
  for (let i = 0; i < d.slots.length; i++) {
    const c = d.slots[i];
    if (segs.length && segs[segs.length - 1].c === c) segs[segs.length - 1].b = i + 1;
    else segs.push({ a: i, b: i + 1, c });
  }
  const n = d.slots.length || 96;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "44px minmax(0, 1fr)", gap: 8, alignItems: "center", marginTop: 5 }}>
      <span style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{d.day.slice(8, 10)}.{d.day.slice(5, 7)}</span>
      <div style={{ position: "relative", height: 12, borderRadius: 4, border: "1px solid var(--border)", overflow: "hidden" }}>
        {segs.filter(s => s.c !== "0").map(s => (
          <div key={s.a} style={{ position: "absolute", top: 0, bottom: 0, left: `${(s.a / n) * 100}%`, width: `${((s.b - s.a) / n) * 100}%`, background: SLOT_COLOR[s.c] }} />
        ))}
      </div>
    </div>
  );
}

export default function UploadCard({ unitHint, onDone }: { unitHint: number | null; onDone?: () => void }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [man, setMan] = useState<UploadManifest | null>(null);
  const [mode, setMode] = useState<"missing" | "replace">("missing");
  const [fD, setFD] = useState(""); const [fT, setFT] = useState("00:00:00");
  const [tD, setTD] = useState(""); const [tT, setTT] = useState("23:59:59");
  const [units, setUnits] = useState<Record<string, number>>({});
  const fileRef = useRef<HTMLInputElement | null>(null);
  const pollRef = useRef<number | null>(null);
  useEffect(() => () => { if (pollRef.current) window.clearInterval(pollRef.current); }, []);

  const reset = () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    setMan(null); setErr(null); setMode("missing"); setUnits({});
  };
  const onPick = async (file: File) => {
    reset(); setBusy(true);
    tg()?.HapticFeedback?.impactOccurred("light");
    try {
      const form = new FormData();
      form.append("file", file);
      const m = await api.upload<UploadManifest>(`/api/uploads/preview${unitHint ? `?unit=${unitHint}` : ""}`, form);
      setMan(m);
      // the default range is the whole upload
      const firsts = m.items.map(i => i.first).filter((x): x is string => !!x).sort();
      const lasts = m.items.map(i => i.last).filter((x): x is string => !!x).sort();
      if (firsts.length && lasts.length) {
        const a = firsts[0], b = lasts[lasts.length - 1];
        setFD(a.slice(0, 10)); setFT(a.slice(11, 19)); setTD(b.slice(0, 10)); setTT(b.slice(11, 19));
      }
    } catch (e) {
      setErr((e as ApiError).message);
      tg()?.HapticFeedback?.notificationOccurred("error");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const commit = async () => {
    if (!man) return;
    setErr(null);
    tg()?.HapticFeedback?.impactOccurred("medium");
    try {
      await api.post(`/api/uploads/${man.token}/commit`, {
        mode, units,
        from_ts: fD ? `${fD}T${fT || "00:00:00"}+00:00` : null,
        to_ts: tD ? `${tD}T${tT || "23:59:59"}+00:00` : null,
      });
      setMan({ ...man, status: "running" });
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(async () => {
        const m = await api.get<UploadManifest>(`/api/uploads/${man.token}`).catch(() => null);
        if (!m) return;
        setMan(m);
        if (m.status === "done" || m.status === "error") {
          if (pollRef.current) window.clearInterval(pollRef.current);
          const bad = m.status === "error" || m.items.some(i => i.status === "error");
          tg()?.HapticFeedback?.notificationOccurred(bad ? "error" : "success");
          onDone?.();
        }
      }, 1500);
    } catch (e) {
      setErr((e as ApiError).message);
    }
  };

  const ready = man ? man.items.filter(i => i.status === "ready") : [];
  const needUnit = ready.filter(i => i.kind === "scada" && !i.unit && !units[String(i.id)]);
  const knownNew = ready.every(i => i.new != null);
  const totalNew = ready.reduce((s, i) => s + (i.new ?? 0), 0);
  const nothingNew = mode === "missing" && knownNew && totalNew === 0;
  const kindLabel = (it: UploadItem) =>
    it.kind === "scada" ? (it.unit || units[String(it.id)] ? `G${it.unit ?? units[String(it.id)]}` : t("up.kind_nounit")) : t("up.kind_" + it.kind);
  const statusLine = (it: UploadItem) => {
    const r = it.result ?? {};
    switch (it.status) {
      case "running": return `⏳ ${t("up.st_running")}`;
      case "done": return `✅ ${t("up.st_done", { n: fmtInt(r.inserted ?? r.rows ?? 0) })}` + (r.events ? ` · ${t("up.st_events", { n: r.events })}` : "");
      case "nothing": return `＝ ${t("up.st_nothing")}`;
      case "error": return `⚠️ ${t("up.st_error")}${it.note ? `: ${it.note}` : ""}`;
      case "skip": return `↷ ${t("up.st_skip")}`;
      default: return man?.status === "preview" ? "" : `⏳ ${t("up.st_ready")}`;
    }
  };
  const inp: React.CSSProperties = { width: "100%", minWidth: 0, boxSizing: "border-box", fontSize: 14, padding: "8px 8px", borderRadius: 9,
    border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)", fontVariantNumeric: "tabular-nums" };
  const radio = (k: "missing" | "replace") => (
    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0", cursor: "pointer" }}>
      <input type="radio" name="upload-mode" checked={mode === k} onChange={() => setMode(k)} style={{ width: 20, height: 20, margin: "2px 0 0", accentColor: "#2481cc" }} />
      <span><span style={{ fontSize: 14, fontWeight: 600 }}>{t("up.mode_" + k)}</span><br />
        <span style={{ fontSize: 12, color: "var(--hint)" }}>{t("up.mode_" + k + "_sub")}</span></span>
    </label>
  );

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{t("up.title")}</div>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 10 }}>{t("up.hint")}</div>
        {(!man || man.status === "preview") && (
          <input ref={fileRef} type="file" accept=".csv,.gz,.zip,.xlsx,.txt" disabled={busy}
            onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
        )}
        {busy && <div style={{ marginTop: 8, fontSize: 13 }}><span className="spinner" /> {t("up.reading")}</div>}
        {err && <div className="alert error" style={{ marginTop: 8 }}>{err}</div>}
      </div>

      {man && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>{t("up.items")}</div>
          <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{man.name}</div>
          {man.items.map(it => (
            <div key={it.id} style={{ borderTop: "1px solid var(--border)", padding: "8px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                <b style={{ fontSize: 14, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</b>
                <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", color: it.kind === "unknown" ? "var(--hint)" : "var(--link)" }}>{kindLabel(it)}</span>
              </div>
              {it.first && it.last && (
                <div style={{ fontSize: 12, color: "var(--hint)", fontVariantNumeric: "tabular-nums" }}>
                  {fmtDT(it.first)} → {fmtDT(it.last)} · {t(it.kind === "alarms" ? "up.alarm_rows" : "up.rows", { n: fmtInt(it.rows) })}
                </div>
              )}
              {it.clock && it.first && (
                <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 1 }}>
                  🕓 {it.clock.applied ? t("up.clock_moved", { shift: it.clock.shift.map(ltr).join(" / ") }) : t("up.clock_as_is")}
                  {it.clock.check?.moved != null && ` · ${t("up.clock_check", { a: fmtInt(it.clock.check.moved), b: fmtInt(it.clock.check.as_is ?? 0) })}`}
                  {it.clock.check?.by_journal && ` · ${t("up.clock_by_journal")}`}
                </div>
              )}
              {it.new != null && it.in_db != null && (
                <div style={{ fontSize: 12.5, marginTop: 2 }}>
                  <span style={{ color: it.new > 0 ? "#1b8a3a" : "var(--hint)", fontWeight: 600 }}>{t("up.new", { n: fmtInt(it.new) })}</span>
                  <span style={{ color: "var(--hint)" }}> · {t("up.in_db", { n: fmtInt(it.in_db) })}</span>
                </div>
              )}
              {it.kind === "scada" && !it.unit && man.status === "preview" && (
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
                  <span style={{ fontSize: 12, color: "var(--hint)" }}>{t("up.pick_unit")}</span>
                  {[1, 2].map(u => (
                    <button key={u} className={"turbine-chip" + (units[String(it.id)] === u ? " active" : "")} style={{ padding: "4px 12px", minHeight: 32 }}
                      onClick={() => setUnits({ ...units, [String(it.id)]: u })}>G{u}</button>
                  ))}
                </div>
              )}
              {(it.days ?? []).slice(0, 31).map(d => <DayBar key={d.day} d={d} />)}
              {(it.days?.length ?? 0) > 31 && <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 4 }}>{t("up.more_days", { n: (it.days?.length ?? 0) - 31 })}</div>}
              {it.note && it.status !== "error" && <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 4 }}>{it.note}</div>}
              {statusLine(it) && <div style={{ fontSize: 13, marginTop: 4 }}>{statusLine(it)}</div>}
            </div>
          ))}
          {man.items.some(i => (i.days?.length ?? 0) > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", fontSize: 11.5, color: "var(--hint)", marginTop: 6 }}>
              {[["1", "up.legend_new"], ["3", "up.legend_part"], ["2", "up.legend_db"]].map(([c, k]) => (
                <span key={c} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 12, height: 10, borderRadius: 2, background: SLOT_COLOR[c] }} />{t(k)}
                </span>
              ))}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 12, height: 10, borderRadius: 2, border: "1px solid var(--border)" }} />{t("up.legend_none")}
              </span>
            </div>
          )}
        </div>
      )}

      {man && man.status === "preview" && ready.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{t("up.how")}</div>
          {radio("missing")}
          {radio("replace")}
          <div style={{ fontSize: 12, color: "var(--hint)", margin: "10px 0 6px" }}>{t("up.range")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) minmax(0, 1fr)", gap: "6px 8px", alignItems: "center" }}>
            <label htmlFor="up-fd" style={{ fontSize: 13, color: "var(--hint)" }}>{t("period.from")}</label>
            <input id="up-fd" type="date" value={fD} onChange={e => setFD(e.target.value)} style={inp} />
            <input id="up-ft" type="time" step={1} value={fT} onChange={e => setFT(e.target.value)} style={inp} aria-label={t("period.from")} />
            <label htmlFor="up-td" style={{ fontSize: 13, color: "var(--hint)" }}>{t("period.to")}</label>
            <input id="up-td" type="date" value={tD} onChange={e => setTD(e.target.value)} style={inp} />
            <input id="up-tt" type="time" step={1} value={tT} onChange={e => setTT(e.target.value)} style={inp} aria-label={t("period.to")} />
          </div>
          {needUnit.length > 0 && <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 8 }}>{t("up.need_unit")}</div>}
          {nothingNew && <div style={{ fontSize: 12.5, color: "var(--hint)", marginTop: 8 }}>{t("up.nothing")}</div>}
          <button className="btn" style={{ marginTop: 12 }} disabled={nothingNew} onClick={commit}>
            {mode === "missing" && knownNew ? t("up.go_new", { n: fmtInt(totalNew) }) : t("up.go")}
          </button>
        </div>
      )}

      {man && man.status !== "preview" && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 14 }}>
            {man.status === "running" ? <><span className="spinner" /> {t("up.running")}</> : man.status === "done" ? `✅ ${t("up.done")}` : `⚠️ ${t("up.st_error")}`}
          </div>
          {man.status !== "running" && (
            <button className="btn secondary" style={{ marginTop: 10 }} onClick={reset}>{t("up.again")}</button>
          )}
        </div>
      )}
    </>
  );
}

// «Журнал алармов» source: what of the station alarm journal is stored.
export function AlarmsSummary() {
  const { t } = useT();
  const [s, setS] = useState<{ count: number; first: string | null; last: string | null } | null>(null);
  useEffect(() => { api.get<{ count: number; first: string | null; last: string | null }>("/api/uploads/alarms/summary").then(setS).catch(() => undefined); }, []);
  if (!s) return <div className="empty-state"><span className="spinner" /></div>;
  return (
    <div className="card">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{t("up.alarms_title")}</div>
      <div style={{ fontSize: 13, color: "var(--hint)" }}>
        {s.first && s.last ? t("up.alarms_cover", { n: fmtInt(s.count), from: fmtDT(s.first).slice(0, 10), to: fmtDT(s.last).slice(0, 10) }) : t("common.no_data")}
      </div>
    </div>
  );
}
